import { useEffect, useState } from "react";
import { siteConfig, showreel } from "../../data/portfolioData";

const TYPED_PHRASES = [
  "Video Editor",
  "Color Grader",
  "Storyteller",
  "Motion Artist",
];

function useTypewriter(phrases) {
  const [displayText, setDisplayText] = useState("");
  const [phraseIndex, setPhraseIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = phrases[phraseIndex];
    let timeout;

    if (!deleting && charIndex < current.length) {
      timeout = setTimeout(() => setCharIndex((c) => c + 1), 80);
    } else if (!deleting && charIndex === current.length) {
      timeout = setTimeout(() => setDeleting(true), 2000);
    } else if (deleting && charIndex > 0) {
      timeout = setTimeout(() => setCharIndex((c) => c - 1), 45);
    } else if (deleting && charIndex === 0) {
      setDeleting(false);
      setPhraseIndex((i) => (i + 1) % phrases.length);
    }

    setDisplayText(current.slice(0, charIndex));
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, phraseIndex, phrases]);

  return displayText;
}

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  const [reelOpen, setReelOpen] = useState(false);
  const typeText = useTypewriter(TYPED_PHRASES);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollToWork = () => {
    document.getElementById("work")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-carbon-950"
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,107,53,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,107,53,0.03) 1px, transparent 1px)
          `,
          backgroundSize: "80px 80px",
        }}
      />
      <div
        className="absolute pointer-events-none"
        style={{
          top: "20%",
          left: "60%",
          width: "600px",
          height: "600px",
          background: "radial-gradient(circle, rgba(255,107,53,0.07) 0%, transparent 65%)",
          transform: "translate(-50%, -50%)",
        }}
      />
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden lg:flex flex-col items-center gap-3">
        <div className="w-px h-16 bg-carbon-700" />
        <span
          className="font-mono text-xs text-ash-600 tracking-widest uppercase"
          style={{ writingMode: "vertical-rl" }}
        >
          Scroll to explore
        </span>
        <div className="w-px h-16 bg-carbon-700" />
      </div>
      <div className="absolute left-8 bottom-12 hidden lg:flex items-center gap-3">
        <div className="w-8 h-px bg-ember-400" />
        <span className="font-mono text-xs text-ash-600 tracking-widest">
          EST. {new Date().getFullYear()}
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-center">
          <div>
            <div
              className="flex items-center gap-3 mb-8"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "none" : "translateY(20px)",
                transition: "all 0.6s ease 0.1s",
              }}
            >
              <div className="w-6 h-px bg-ember-400" />
              <span className="font-mono text-xs text-ember-400 tracking-widest uppercase">
                Portfolio 2026
              </span>
              {siteConfig.availableForWork && (
                <span className="ml-2 flex items-center gap-1.5 bg-carbon-800 border border-carbon-700 px-2.5 py-1 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="font-mono text-xs text-green-400 tracking-wider">Available</span>
                </span>
              )}
            </div>
            <h1
              className="font-display text-[clamp(4rem,10vw,9rem)] leading-none tracking-wider mb-2 text-ash-100"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "none" : "translateY(40px)",
                transition: "all 0.8s ease 0.2s",
              }}
            >
              <span className="glitch-text" data-text={siteConfig.name.split(" ")[0]}>
                {siteConfig.name.split(" ")[0]}
              </span>
            </h1>
            <h1
              className="font-display text-[clamp(4rem,10vw,9rem)] leading-none tracking-wider mb-8 text-ember-400 ember-glow-text"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "none" : "translateY(40px)",
                transition: "all 0.8s ease 0.35s",
              }}
            >
              {siteConfig.name.split(" ")[1]}
            </h1>
            <div
              className="flex items-center gap-3 mb-10"
              style={{ opacity: loaded ? 1 : 0, transition: "all 0.8s ease 0.5s" }}
            >
              <div className="w-3 h-px bg-ash-600" />
              <span className="font-heading text-2xl lg:text-3xl text-ash-400 tracking-widest uppercase font-light">
                {typeText}
                <span className="cursor-blink text-ember-400 ml-0.5">|</span>
              </span>
            </div>
            <p
              className="font-body text-ash-400 text-base lg:text-lg leading-relaxed max-w-lg mb-10"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "none" : "translateY(20px)",
                transition: "all 0.8s ease 0.65s",
              }}
            >
              {siteConfig.subTagline}. Turning raw footage into brand-defining stories
              for agencies and clients that demand excellence.
            </p>
            <div
              className="flex items-center gap-4 flex-wrap"
              style={{
                opacity: loaded ? 1 : 0,
                transform: loaded ? "none" : "translateY(20px)",
                transition: "all 0.8s ease 0.8s",
              }}
            >
              <button
                onClick={scrollToWork}
                className="group relative px-8 py-3.5 bg-ember-400 text-carbon-950 font-heading text-base tracking-widest uppercase overflow-hidden transition-all duration-300 hover:bg-ember-500"
              >
                <span className="relative z-10">View Work</span>
              </button>
              <button
                onClick={() => setReelOpen(true)}
                className="group flex items-center gap-3 px-8 py-3.5 border border-carbon-600 text-ash-200 font-heading text-base tracking-widest uppercase hover:border-ember-400 hover:text-ember-400 transition-all duration-300"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                  <polygon points="2,1 13,7 2,13" />
                </svg>
                Watch Reel
              </button>
            </div>
          </div>

          <div
            className="relative"
            style={{
              opacity: loaded ? 1 : 0,
              transform: loaded ? "none" : "translateX(60px)",
              transition: "all 1s ease 0.4s",
            }}
          >
            <div className="absolute -inset-3 border border-ember-400/20" />
            <div className="absolute -top-6 -right-6 w-12 h-12 border-t border-r border-ember-400/40" />
            <div className="absolute -bottom-6 -left-6 w-12 h-12 border-b border-l border-ember-400/40" />

            {/* Always-playing looped muted video — click opens modal with sound */}
            <div
              className="relative w-full overflow-hidden bg-carbon-800 cursor-pointer"
              onClick={() => setReelOpen(true)}
            >
              <div className="video-aspect">
                <iframe
                  src={`https://www.youtube.com/embed/${showreel.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${showreel.youtubeId}&controls=0&modestbranding=1&rel=0&playsinline=1`}
                  title={showreel.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  className="absolute inset-0 w-full h-full border-0 z-10"
                />
                {/* Blocks clicks reaching iframe — prevents YouTube pause */}
                <div className="absolute inset-0" style={{ zIndex: 15 }} />
              </div>

              {/* Label */}
              {/* Bottom gradient overlay for text visibility */}
              <div
                className="absolute inset-x-0 bottom-0 pointer-events-none"
                style={{
                  zIndex: 19,
                  height: "30%",
                  background: "linear-gradient(to top, rgba(8,8,8,0.85) 0%, rgba(8,8,8,0.4) 50%, transparent 100%)",
                }}
              />

              {/* Label */}
              <div
                className="absolute inset-0 flex items-end justify-between p-4 pointer-events-none"
                style={{ zIndex: 20 }}
              >
                <div>
                  <p className="font-mono text-xs text-ash-400 tracking-widest uppercase">Showreel</p>
                  <p className="font-heading text-xl text-ash-100 tracking-wide">{showreel.title}</p>
                </div>
                <span className="font-mono text-xs text-ember-400 border border-ember-400/40 px-2 py-1">
                  PLAY
                </span>
              </div>
            </div>

            <div className="grid grid-cols-3 border border-carbon-800 mt-0">
              {[
                { n: "1+", l: "Years" },
                { n: "30+", l: "Projects" },
                { n: "15+", l: "Clients" },
              ].map((s, i) => (
                <div
                  key={i}
                  className={`py-4 text-center ${i < 2 ? "border-r border-carbon-800" : ""}`}
                >
                  <div className="font-display text-2xl text-ember-400">{s.n}</div>
                  <div className="font-mono text-xs text-ash-600 tracking-widest uppercase mt-1">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {reelOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-carbon-950/95 backdrop-blur-sm"
          onClick={() => setReelOpen(false)}
        >
          <div
            className="relative w-full max-w-5xl mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setReelOpen(false)}
              className="absolute -top-10 right-0 font-mono text-xs text-ash-400 hover:text-ember-400 tracking-widest uppercase flex items-center gap-2"
            >
              Close ✕
            </button>
            <div className="video-aspect border border-carbon-700">
              <iframe
                key={reelOpen ? "open" : "closed"}
                src={`https://www.youtube.com/embed/${showreel.youtubeId}?autoplay=1&mute=0&rel=0&modestbranding=1&enablejsapi=1&playsinline=1`}
                title={showreel.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <div className="w-px h-8 bg-gradient-to-b from-transparent to-ash-600" />
        <div className="w-1 h-1 rounded-full bg-ash-600" />
      </div>
    </section>
  );
}